import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';

interface ServiceGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ServiceGuideModal: React.FC<ServiceGuideModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Backdrop onClick={handleBackdropClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>서비스 이용안내</ModalTitle>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </ModalHeader>

        <ModalContent>
          <Section>
            <SectionTitle>서비스 이용안내</SectionTitle>
            <List>
              <ListItem>
                고객은 구입하고자 하는 컨텐츠를 결제를 완료한 후 열람 서비스를 이용할 수 있습니다.
              </ListItem>
              <ListItem>
                고객은 구입한 컨텐츠의 제공하는 정보의 종류, 가격, 보관기간, 이용 횟수 및 기간 등 이용조건을 숙지하고 정해진 조건에 맞게 이용하여야 합니다.
              </ListItem>
              <ListItem>
                컨텐츠는 결제를 완료한 후, 즉시 열람 이용이 가능합니다.
              </ListItem>
            </List>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>환불 및 취소 규정</SectionTitle>

            <Article>
              <ArticleTitle>제1조 (목적)</ArticleTitle>
              <Paragraph>
                본 규정은 럭키온(이하 "회사")가 제공하는 디지털 콘텐츠 및 유료 서비스(이하 "유료콘텐츠")의 결제, 취소 및 환불과 관련된 기준을 명확히 하여, 이용자의 권익을 보호하고 공정한 거래 질서를 확립함을 목적으로 합니다.
              </Paragraph>
            </Article>

            <Article>
              <ArticleTitle>제2조 (유료콘텐츠의 특성)</ArticleTitle>
              <List>
                <ListItem>
                  회사가 제공하는 유료콘텐츠는 사주, 운세 결과 등 비실물 디지털 상품입니다.
                </ListItem>
                <ListItem>
                  이러한 유료콘텐츠는 구매 즉시 열람이 가능하므로, 이용이 시작된 이후에는 환불이 제한됩니다.
                </ListItem>
              </List>
            </Article>

            <Article>
              <ArticleTitle>제3조 (취소 및 환불 가능 조건)</ArticleTitle>
              <List>
                <ListItem>
                  이용자가 결제를 완료했으나 유료콘텐츠를 열람하지 않은 경우, 결제일로부터 7일 이내에 환불을 요청할 수 있습니다.
                </ListItem>
                <ListItem>
                  결제 과정에서 시스템 오류, 중복 결제, 금액 착오 등이 확인될 경우 전액 환불합니다.
                </ListItem>
                <ListItem>
                  환불 요청은 고객센터(전화/이메일)를 통해 접수해야 하며, 회사는 이용자의 신원 확인 후 처리합니다.
                </ListItem>
              </List>
            </Article>

            <Article>
              <ArticleTitle>제4조 (환불이 불가능한 경우)</ArticleTitle>
              <Paragraph>다음 각 호에 해당하는 경우 환불이 불가합니다.</Paragraph>
              <List>
                <ListItem>유료콘텐츠를 열람, 이용한 경우</ListItem>
                <ListItem>
                  이용자의 단순 변심, 기기·환경적 문제(호환성, 저장공간 부족 등)
                </ListItem>
                <ListItem>이벤트·할인·프로모션을 통해 제공된 상품</ListItem>
                <ListItem>
                  회사의 귀책사유가 없는 네트워크 장애, 기기 오류 등 외부 요인
                </ListItem>
              </List>
              <Note>
                ※ 「전자상거래 등에서의 소비자보호에 관한 법률」 제17조 제2항에 따라, 콘텐츠의 성격상 이용이 개시된 경우 청약철회가 제한됩니다.
              </Note>
            </Article>

            <Article>
              <ArticleTitle>제5조 (환불 절차 및 기한)</ArticleTitle>
              <List>
                <ListItem>
                  환불은 원칙적으로 결제에 사용한 동일 결제 수단으로만 가능합니다.
                </ListItem>
                <ListItem>
                  카드사 및 결제 대행사 사정에 따라 환불 완료까지 영업일 기준 최대 7일이 소요될 수 있습니다.
                </ListItem>
                <ListItem>
                  환불 과정에서 발생하는 수수료(해외 결제 수수료, 환율 변동 등)는 이용자에게 부담될 수 있습니다.
                </ListItem>
              </List>
            </Article>

            <Article>
              <ArticleTitle>제6조 (소비자의 권리 보장)</ArticleTitle>
              <List>
                <ListItem>
                  본 규정은 「전자상거래법」 및 관련 법령을 준수합니다.
                </ListItem>
                <ListItem>
                  이용자는 본 규정에서 정하지 않은 사항에 대하여 관계 법령 및 공정거래위원회의 소비자 분쟁 해결 기준을 따를 수 있습니다.
                </ListItem>
              </List>
            </Article>

            <ContactInfo>
              문의 사항이 있으시면 <ContactLink href="tel:1661-2847">고객센터</ContactLink>로 연락해 주세요
            </ContactInfo>
          </Section>
        </ModalContent>
      </ModalContainer>
    </Backdrop>
  );
};

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${tokens.spacing[4]};
  overflow-y: auto;
`;

const ModalContainer = styled.div`
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.borderRadius.lg};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${tokens.shadows['2xl']};
  margin: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${tokens.spacing[6]} ${tokens.spacing[6]} ${tokens.spacing[4]};
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[900]};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${tokens.typography.fontSize['2xl']};
  color: ${tokens.colors.neutral[500]};
  cursor: pointer;
  padding: ${tokens.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tokens.borderRadius.md};
  transition: all ${tokens.animation.duration.fast} ${tokens.animation.easing.ease};
  width: 32px;
  height: 32px;

  &:hover {
    background-color: ${tokens.colors.neutral[100]};
    color: ${tokens.colors.neutral[900]};
  }
`;

const ModalContent = styled.div`
  padding: ${tokens.spacing[6]};
  overflow-y: auto;
  flex: 1;
`;

const Section = styled.section`
  margin-bottom: ${tokens.spacing[8]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[900]};
  margin: 0 0 ${tokens.spacing[4]} 0;
`;

const Article = styled.article`
  margin-bottom: ${tokens.spacing[6]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ArticleTitle = styled.h4`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin: 0 0 ${tokens.spacing[3]} 0;
`;

const Paragraph = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.regular};
  color: ${tokens.colors.neutral[700]};
  line-height: 1.7;
  margin: 0 0 ${tokens.spacing[3]} 0;
  word-break: keep-all;
`;

const List = styled.ol`
  margin: 0;
  padding-left: ${tokens.spacing[6]};
  list-style-type: decimal;
`;

const ListItem = styled.li`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.regular};
  color: ${tokens.colors.neutral[700]};
  line-height: 1.7;
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Note = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.regular};
  color: ${tokens.colors.neutral[600]};
  line-height: 1.6;
  margin: ${tokens.spacing[3]} 0 0 0;
  padding: ${tokens.spacing[3]};
  background-color: ${tokens.colors.neutral[50]};
  border-radius: ${tokens.borderRadius.md};
  border-left: 3px solid ${tokens.colors.primary[500]};
  word-break: keep-all;
`;

const ContactInfo = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[800]};
  line-height: 1.7;
  margin: ${tokens.spacing[6]} 0 0 0;
  text-align: center;
  word-break: keep-all;
`;

const ContactLink = styled.a`
  color: ${tokens.colors.primary[500]};
  text-decoration: underline;
  transition: opacity ${tokens.animation.duration.fast} ${tokens.animation.easing.ease};

  &:hover {
    opacity: 0.8;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(90deg, transparent, ${tokens.colors.neutral[300]}, transparent);
  margin: ${tokens.spacing[8]} 0;
`;
